import React, { useContext, useEffect, useMemo, useState } from "react";
import { Dialog } from "@actinc/dls/components";
import { AlertContext } from "@actinc/dls/context";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  TextField,
} from "@mui/material";
import { Controller, useForm, useFormState } from "react-hook-form";
import { useMutation } from "urql";

import { ControlledFormInput } from "../../../../../components";
import { getUserOptions } from "../../../../../services/utils";
import { UserContext } from "../../../../../userProvider";
import { UsersContext } from "../../../../../usersProvider";

const EXTERNAL_REVIEWER = "External Reviewer";

const ReviewGroupDialog = ({
  closeDialog,
  existingGroups = [],
  group,
  programID,
}) => {
  const user = useContext(UserContext);
  const users = useContext(UsersContext);
  const alertContext = useContext(AlertContext);
  const [, mutateGroup] = useMutation(
    group
      ? updateExternalReviewerGroupMutation
      : createExternalReviewerGroupMutation,
  );

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { name: "", reviewers: [] },
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { isSubmitting, isValid } = useFormState({ control });
  const [hasInitializedForm, setHasInitializedForm] = useState(false);

  // Eligible reviewers: active users who hold the External Reviewer review type
  // on this program. Reuses getUserOptions so the pool matches the reviewer
  // picker used when assigning External Review tasks from search.
  const reviewerOptions = useMemo(
    () =>
      getUserOptions(users)({
        programs: [programID],
        roles: [EXTERNAL_REVIEWER],
      }),
    [users, programID],
  );

  // Populate the form once the reviewer pool is available (edit mode needs the
  // pool to resolve stored reviewer IDs into option objects).
  useEffect(() => {
    if (hasInitializedForm) return;
    if (group) {
      if (!users?.length) return;
      const selected = reviewerOptions.filter((option) =>
        (group.reviewers || []).includes(option.value),
      );
      reset({ name: group.name || "", reviewers: selected });
    }
    setHasInitializedForm(true);
  }, [group, hasInitializedForm, reset, reviewerOptions, users]);

  // Group names must be unique within the program (case-insensitive). The DB
  // enforces this too via a unique index; this gives a friendly inline error.
  const isNameTaken = (value) => {
    const normalized = value.trim().toLowerCase();
    return existingGroups.some(
      (existing) =>
        existing.id !== group?.id &&
        (existing.name || "").trim().toLowerCase() === normalized,
    );
  };

  const onSubmit = async (formData) => {
    const stamp = {
      id: user.id,
      name: [user.lastName, user.firstName].filter(Boolean).join(", "),
    };
    const groupInput = {
      name: formData.name.trim(),
      program: programID,
      reviewers: formData.reviewers.map((option) => option.value),
      ...(group ? { modifiedBy: stamp } : { createdBy: stamp }),
    };

    const result = await mutateGroup(
      group ? { data: groupInput, id: group.id } : { data: groupInput },
    );
    if (result.error) {
      console.error("External Reviewer Group mutation error:", result.error);
      alertContext.actions.addAlert({
        message: `Failed to ${group ? "update" : "create"} review group "${groupInput.name}". Please try again.`,
        options: { variant: "error" },
      });
      return;
    }
    closeDialog(true);
  };

  const dialogProps = {
    draggable: false,
    fullWidth: true,
    maxWidth: "sm",
    onClose: () => closeDialog(false),
    open: true,
    scroll: "paper",
    title: group ? "Edit Review Group" : "Create Review Group",
  };

  return (
    <Dialog {...dialogProps}>
      <DialogContent dividers={true} sx={{ paddingTop: 2 }}>
        <form id="reviewGroupForm" onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ marginBottom: 3 }}>
            <ControlledFormInput
              control={control}
              fullWidth={true}
              id="reviewGroup-name"
              label="Group Name"
              name="name"
              rules={{
                required: "Group Name is required",
                validate: {
                  notBlank: (value) =>
                    value.trim().length > 0 || "Group Name is required",
                  unique: (value) =>
                    !isNameTaken(value) ||
                    "A group with this name already exists for this program",
                },
              }}
            />
          </Box>
          <Controller
            control={control}
            name="reviewers"
            rules={{
              validate: (value) =>
                value.length > 0 || "Select at least one reviewer",
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Autocomplete
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, val) =>
                  option.value === val.value
                }
                multiple
                onChange={(event, newValue) => onChange(newValue)}
                options={reviewerOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={Boolean(error)}
                    helperText={error?.message}
                    label="Reviewers"
                    placeholder="Select reviewers"
                  />
                )}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option.label}
                      {...getTagProps({ index })}
                      key={option.value}
                    />
                  ))
                }
                value={value}
              />
            )}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => closeDialog(false)} variant="text">
          Cancel
        </Button>
        <Button
          disabled={!isValid || isSubmitting}
          form="reviewGroupForm"
          type="submit"
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const createExternalReviewerGroupMutation = `
  mutation CreateExternalReviewerGroup($data: ExternalReviewerGroupInput!) {
    createExternalReviewerGroup(data: $data) {
      id
    }
  }`;

const updateExternalReviewerGroupMutation = `
  mutation UpdateExternalReviewerGroup($data: ExternalReviewerGroupInput!, $id: ID!) {
    updateExternalReviewerGroup(data: $data, id: $id) {
      id
    }
  }`;

export {
  createExternalReviewerGroupMutation,
  ReviewGroupDialog,
  updateExternalReviewerGroupMutation,
};
